<script>
import { mapGetters } from 'vuex';
import { useAutomation } from './useAutomation';
import { useEditableAutomation } from './useEditableAutomation';
import FilterInputBox from './components/FilterInputBox.vue';
import AutomationActionInput from './components/AutomationActionInput.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import {
  getFileName,
  generateAutomationPayload,
  getAttributes,
  getInputType,
  getOperators,
  showActionInput,
} from './automationHelper';
import { validateAutomation } from './validation';

import { getAutomationRuleEvents, getAutomationActionTypes } from './constants';
import {
  getActionKeysForEvent,
  getEventContexts,
  getEventsByContext,
} from './event-entity-map';

export default {
  components: {
    FilterInputBox,
    NextButton,
    SingleSelect,
    AutomationActionInput,
  },
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    selectedResponse: {
      type: Object,
      default: null,
    },
  },
  emits: ['saveAutomation'],
  setup() {
    const {
      automation,
      automationTypes,
      onEventChange,
      getConditionDropdownValues,
      appendNewCondition,
      appendNewAction,
      removeFilter,
      removeAction,
      resetFilter,
      getActionDropdownValues,
      manifestCustomAttributes,
    } = useAutomation();
    const { formatAutomation } = useEditableAutomation();
    return {
      automation,
      automationTypes,
      onEventChange,
      getConditionDropdownValues,
      appendNewCondition,
      appendNewAction,
      removeFilter,
      removeAction,
      resetFilter,
      getActionDropdownValues,
      formatAutomation,
      manifestCustomAttributes,
    };
  },
  data() {
    return {
      selectedContext: 'conversation',
      automationRuleEvent: getAutomationRuleEvents()[0].key,
      automationMutated: false,
      show: true,
      showDeleteConfirmationModal: false,
      allCustomAttributes: [],
      errors: {},
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    // Determina se é modo edição ou criação
    isEditMode() {
      return !!(this.selectedResponse && this.selectedResponse.id);
    },
    mode() {
      return this.isEditMode ? 'edit' : 'create';
    },
    modalTitle() {
      return this.isEditMode
        ? this.$t('AUTOMATION.EDIT.TITLE')
        : this.$t('AUTOMATION.ADD.TITLE');
    },
    submitButtonText() {
      return this.isEditMode
        ? this.$t('AUTOMATION.EDIT.SUBMIT')
        : this.$t('AUTOMATION.ADD.SUBMIT');
    },
    eventContexts() {
      // Only show contexts that have at least one event defined
      return Object.entries(getEventContexts())
        .filter(([key]) => getEventsByContext(key).length > 0)
        .map(([key, value]) => ({
          key,
          label: value.label,
          icon: value.icon,
        }));
    },
    contextOptions() {
      return this.eventContexts.map(context => ({
        id: context.key,
        name: `${context.icon} ${context.label}`.trim(),
      }));
    },
    selectedContextSelectModel: {
      get() {
        return (
          this.contextOptions.find(
            option => option.id === this.selectedContext
          ) || null
        );
      },
      set(value) {
        this.selectedContext = value?.id || '';
        this.onContextChange();
      },
    },
    automationRuleEvents() {
      return getEventsByContext(this.selectedContext).map(event => ({
        ...event,
        // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
        displayLabel: this.$t(`AUTOMATION.EVENTS.${event.value}`),
      }));
    },
    eventOptions() {
      return this.automationRuleEvents.map(event => ({
        id: event.key,
        name: event.displayLabel,
      }));
    },
    selectedEventSelectModel: {
      get() {
        return (
          this.eventOptions.find(
            option => option.id === this.automation.event_name
          ) || null
        );
      },
      set(value) {
        this.automation.event_name = value?.id || '';
        this.onEventChange();
      },
    },
    hasAutomationMutated() {
      if (
        this.automation.conditions?.[0]?.values ||
        this.automation.actions?.[0]?.action_params?.length
      )
        return true;
      return false;
    },
    automationActionTypes() {
      return this.resolveAutomationActionTypes(this.automation.event_name);
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('agents/get');
    this.$store.dispatch('contacts/get');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('labels/get');
    this.$store.dispatch('campaigns/get');
    const customAttrs = this.$store.getters['attributes/getAttributes'];
    this.allCustomAttributes = Array.isArray(customAttrs) ? customAttrs : [];
    this.manifestCustomAttributes();

    // Se modo edição, carrega dados existentes
    if (this.isEditMode) {
      const formattedAutomation = this.formatAutomation(
        this.selectedResponse,
        this.allCustomAttributes,
        this.automationTypes,
        this.resolveAutomationActionTypes(this.selectedResponse.event_name)
      );

      // Atualiza propriedades do ref reativo ao invés de sobrescrever
      Object.assign(this.automation, formattedAutomation);

      // Define contexto baseado no evento atual
      const currentEvent = getAutomationRuleEvents().find(
        e => e.key === this.automation.event_name
      );
      if (currentEvent) {
        this.selectedContext = currentEvent.context;
      }
    } else {
      // Modo criação: inicializa automação vazia
      const firstEvent = getAutomationRuleEvents()[0];
      Object.assign(this.automation, {
        name: '',
        description: '',
        event_name: firstEvent.key,
        conditions: [
          {
            attribute_key: '',
            filter_operator: 'equal_to',
            values: '',
            query_operator: 'and',
            custom_attribute_type: '',
          },
        ],
        actions: [
          {
            action_name: 'send_message',
            action_params: [],
          },
        ],
      });
      this.selectedContext = firstEvent.context;
    }
  },
  methods: {
    getFileName,
    getAttributes,
    getInputType,
    getOperators,
    showActionInput,
    resolveAutomationActionTypes(eventName) {
      const availableActionKeys = getActionKeysForEvent(eventName);
      const actionTypes = this.isFeatureEnabled('sla')
        ? getAutomationActionTypes()
        : getAutomationActionTypes().filter(({ key }) => key !== 'add_sla');

      return actionTypes
        .filter(action => availableActionKeys.includes(action.key))
        .map(action => ({
          ...action,
          // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
          label: this.$t(`AUTOMATION.ACTIONS.${action.label}`),
        }));
    },
    isFeatureEnabled(flag) {
      return this.isFeatureEnabledonAccount(this.accountId, flag);
    },
    onContextChange() {
      const firstEvent = this.automationRuleEvents[0];
      if (firstEvent) {
        this.automation.event_name = firstEvent.key;
        this.onEventChange();
      }
    },
    commitPendingConditionInputs() {
      const conditionRefs = Array.isArray(this.$refs.conditionRefs)
        ? this.$refs.conditionRefs
        : [this.$refs.conditionRefs].filter(Boolean);

      conditionRefs.forEach(ref => {
        ref?.commitPendingTagInput?.();
      });
    },
    emitSaveAutomation() {
      this.commitPendingConditionInputs();
      this.errors = validateAutomation(this.automation);
      if (Object.keys(this.errors).length === 0) {
        const automation = generateAutomationPayload(this.automation);
        this.$emit('saveAutomation', automation, this.mode);
      }
    },
    getTranslatedAttributes(type, event) {
      return getAttributes(type, event).map(attribute => {
        const skipTranslation =
          attribute.customAttributeType ||
          [
            'contact_custom_attribute',
            'conversation_custom_attribute',
          ].includes(attribute.key);

        if (skipTranslation) {
          return { ...attribute };
        }

        /* eslint-disable @intlify/vue-i18n/no-dynamic-keys */
        const i18nKey = `AUTOMATION.ATTRIBUTES.${attribute.name}`;
        const translatedName = this.$te(i18nKey)
          ? this.$t(i18nKey)
          : attribute.displayName || attribute.name;
        /* eslint-enable @intlify/vue-i18n/no-dynamic-keys */

        return { ...attribute, name: translatedName };
      });
    },
    getCustomAttributeType(customAttributes, key) {
      if (!Array.isArray(customAttributes) || !key) return null;
      const attr = customAttributes.find(a => a.attribute_key === key);
      return attr?.attribute_model || null;
    },
    resetAction(index) {
      const actionName = this.automation.actions[index]?.action_name;
      const defaultParamsByAction = {
        delay_next_action: { value: '', unit: 'seconds' },
        delay_next_action_range: {
          min_value: '',
          min_unit: 'seconds',
          max_value: '',
          max_unit: 'seconds',
        },
        kanban_move_to_stage: {
          funnel_id: null,
          funnel_stage: null,
        },
      };

      this.automation.actions[index] = {
        ...this.automation.actions[index],
        action_params: defaultParamsByAction[actionName] || [],
      };
    },
  },
};
</script>

<template>
  <div>
    <woot-modal-header :header-title="modalTitle">
      <span
        class="mt-1 text-xs font-medium text-n-teal-10 bg-n-teal-2 dark:bg-n-teal-3/20 px-2 py-0.5 rounded inline-block"
      >
        {{ $t('AUTOMATION.ENHANCED.BADGE') }}
      </span>
    </woot-modal-header>
    <div class="flex flex-col modal-content">
      <div class="w-full">
        <woot-input
          v-model="automation.name"
          :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
          type="text"
          :class="{ error: errors.name }"
          :error="errors.name ? $t('AUTOMATION.ADD.FORM.NAME.ERROR') : ''"
          :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
        />
        <woot-input
          v-model="automation.description"
          :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
          type="text"
          :class="{ error: errors.description }"
          :error="
            errors.description ? $t('AUTOMATION.ADD.FORM.DESC.ERROR') : ''
          "
          :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
        />

        <!-- Context and Event Selection (Two columns) -->
        <div class="grid grid-cols-2 gap-4 mb-6">
          <!-- Context Selector -->
          <div>
            <label class="block mb-2">
              {{ $t('AUTOMATION.ADD.FORM.CONTEXT.LABEL') }}
            </label>
            <SingleSelect
              v-model="selectedContextSelectModel"
              :options="contextOptions"
              disable-deselect
              class="automation-rule-form__select"
            />
          </div>

          <!-- Event Selector -->
          <div>
            <label :class="{ error: errors.event_name }" class="block mb-2">
              {{ $t('AUTOMATION.ADD.FORM.EVENT.LABEL') }}
            </label>
            <SingleSelect
              v-model="selectedEventSelectModel"
              :options="eventOptions"
              disable-deselect
              class="automation-rule-form__select"
            />
            <span v-if="errors.event_name" class="message">
              {{ $t('AUTOMATION.ADD.FORM.EVENT.ERROR') }}
            </span>
          </div>
        </div>

        <!-- Reset Message -->
        <p
          v-if="hasAutomationMutated"
          class="text-xs text-right text-n-teal-10 pt-1 mb-6"
        >
          {{ $t('AUTOMATION.FORM.RESET_MESSAGE') }}
        </p>

        <!-- // Conditions Start -->
        <section>
          <label>
            {{ $t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL') }}
          </label>
          <div
            class="w-full p-4 mb-4 border border-solid rounded-lg bg-n-slate-2 dark:bg-n-solid-2 border-n-strong"
          >
            <FilterInputBox
              v-for="(condition, i) in automation.conditions"
              ref="conditionRefs"
              :key="i"
              v-model="automation.conditions[i]"
              :filter-attributes="
                getTranslatedAttributes(automationTypes, automation.event_name)
              "
              :input-type="
                getInputType(
                  allCustomAttributes,
                  automationTypes,
                  automation.event_name,
                  automation.conditions[i].attribute_key
                )
              "
              :operators="
                getOperators(
                  allCustomAttributes,
                  automationTypes,
                  automation.event_name,
                  automation.conditions[i].attribute_key
                )
              "
              :dropdown-values="
                getConditionDropdownValues(
                  automation.conditions[i].attribute_key
                )
              "
              :show-query-operator="i !== automation.conditions.length - 1"
              :custom-attribute-type="
                getCustomAttributeType(
                  allCustomAttributes,
                  automation.conditions[i].attribute_key
                )
              "
              :error-message="errors.conditions ? errors.conditions[i] : ''"
              @reset-filter="resetFilter(i, automation.conditions[i])"
              @remove-filter="removeFilter(i)"
            />
            <div class="flex justify-end w-full gap-2 mt-3">
              <NextButton
                icon="add"
                size="sm"
                color-scheme="secondary"
                @click="appendNewCondition"
              >
                {{ $t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL') }}
              </NextButton>
            </div>
          </div>
        </section>
        <!-- // Conditions End -->
        <!-- // Actions Start -->
        <section>
          <label>
            {{ $t('AUTOMATION.ADD.FORM.ACTIONS.LABEL') }}
          </label>
          <div
            class="w-full p-4 mb-4 border border-solid rounded-lg bg-n-slate-2 dark:bg-n-solid-2 border-n-strong"
          >
            <AutomationActionInput
              v-for="(action, i) in automation.actions"
              :key="i"
              v-model="automation.actions[i]"
              :action-types="automationActionTypes"
              :dropdown-values="
                getActionDropdownValues(automation.actions[i].action_name)
              "
              :show-action-input="
                showActionInput(
                  automationActionTypes,
                  automation.actions[i].action_name
                )
              "
              :show-remove-button="automation.actions.length > 1"
              :error-message="errors.actions ? errors.actions[i] : ''"
              :initial-file-name="
                getFileName(automation.actions[i], automation.files)
              "
              @reset-action="resetAction(i)"
              @remove-action="removeAction(i)"
            />
            <div class="flex justify-end w-full gap-2">
              <NextButton
                icon="add"
                size="sm"
                color-scheme="secondary"
                @click="appendNewAction"
              >
                {{ $t('AUTOMATION.ADD.ACTION_BUTTON_LABEL') }}
              </NextButton>
            </div>
          </div>
        </section>
        <!-- // Actions End -->
        <div class="flex items-center justify-end w-full gap-2">
          <NextButton color-scheme="secondary" @click="onClose">
            {{ $t('AUTOMATION.ADD.CANCEL_BUTTON_TEXT') }}
          </NextButton>
          <NextButton @click="emitSaveAutomation">
            {{ submitButtonText }}
          </NextButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.automation-rule-form__select {
  @apply w-full;
}

.automation-rule-form__select:deep(button) {
  @apply w-full justify-between;
}
</style>
